import * as XLSX from 'xlsx';

import type { AnalysisPercentileRowDto } from '@/api/campaign-analysis-client';
import { csvEscapeCell } from '@/lib/param-format';
import type { RankingViewRow } from '@/lib/analysis-fusion';
import { RANK_PRESENTATION, seniorityLabel } from '@/lib/rank-labels';

// =============================================================================
// benchmark-export.ts — Writer PUR de l'export de la page Analyse fusionnée (lot U1).
// =============================================================================
// DÉCISION 12 (GATE_HUMAN John 2026-09-17, ADR-FUSION-ANALYSE-RANG-01) : l'export de la page porte DEUX
// cases — « Distribution anonymisée » et « Ranking nominatif » —, cochées par défaut, et :
//   • XLSX = UN classeur à DEUX feuilles (une par case cochée) ;
//   • CSV  = DEUX téléchargements successifs (un fichier par case cochée) — « procède avec a. » ;
//     l'archive zip de la maquette est ABANDONNÉE (aucune dépendance de compression n'est ajoutée).
//   • SUPPRIMÉS : la sélection de colonnes et l'option d'anonymisation facultative. La distribution est
//     anonymisée PAR CONSTRUCTION (aucune raison sociale, aucun SIRET), le ranking nominatif par
//     construction : le destinataire se choisit par la case cochée, jamais par une option.
//
// SÉCURITÉ (OBS-1 / R3-3) : chaque cellule string du CSV passe par `csvEscapeCell` (importé de
// param-format — JAMAIS réimplémenté) : préfixe apostrophe si tête dangereuse (= + - @ \t \r), puis
// échappement RFC 4180.
//
// K-ANONYMITY : une cellule MASQUÉE (couple sous le seuil) est exportée VIDE, jamais 0 — parité stricte
// avec l'écran (décision 3 : « — » signale un masquage, jamais une valeur nulle ni un écart de 0 €). La
// feuille de distribution exporte ses lignes supprimées avec des cellules VIDES (le couple existe, ses
// bornes sont masquées).

/** Clé de colonne stable (contrat de journalisation) doublée de son libellé FR d'en-tête. */
export interface BenchmarkExportColumn {
  readonly key: string;
  readonly label: string;
}

/**
 * Colonnes de la feuille « Distribution anonymisée » — ORDRE FIGÉ, calqué sur la card de l'écran
 * (PROFIL · SÉNIORITÉ · MIN · P10 · P25 · P50 · P75 · P90 · MAX). AUCUNE colonne nominative : ni raison
 * sociale, ni SIRET (ST-RANK-06 résultat 6).
 */
export const DISTRIBUTION_EXPORT_COLUMNS: readonly BenchmarkExportColumn[] = [
  { key: 'profileCode', label: 'Profil' },
  { key: 'seniorityLevel', label: 'Séniorité' },
  { key: 'minEur', label: 'MIN' },
  { key: 'p10Eur', label: 'P10' },
  { key: 'p25Eur', label: 'P25' },
  { key: 'p50Eur', label: 'P50' },
  { key: 'p75Eur', label: 'P75' },
  { key: 'p90Eur', label: 'P90' },
  { key: 'maxEur', label: 'MAX' },
];

/**
 * Colonnes de la feuille « Ranking nominatif » — ORDRE FIGÉ, calqué sur le tableau de l'écran (DBU §2.6
 * v2.8 : Sous-famille · Profil · Séniorité · TJM C1 · TJM C2 · Écart médiane · Écart prix cible ·
 * Fournisseur · SIRET · Rang · Percentile). La sous-famille ouvre la liste depuis U1-bis, comme la
 * PREMIÈRE colonne de l'écran (retour GATE_HUMAN 2026-09-18). Le livrable client se produit à partir de
 * CETTE sortie (décision 8) : l'écran et l'export ne divergent jamais.
 */
export const RANKING_EXPORT_COLUMNS: readonly BenchmarkExportColumn[] = [
  { key: 'sousFamille', label: 'Sous-famille' },
  { key: 'profileCode', label: 'Profil' },
  { key: 'seniorityLevel', label: 'Séniorité' },
  { key: 'responseC1Eur', label: 'TJM C1' },
  { key: 'responseC2Eur', label: 'TJM C2' },
  { key: 'ecartP50Pct', label: 'Écart médiane' },
  { key: 'ecartTargetPricePct', label: 'Écart prix cible' },
  { key: 'raisonSociale', label: 'Fournisseur' },
  { key: 'siret', label: 'SIRET' },
  { key: 'rank', label: 'Rang' },
  { key: 'percentile', label: 'Percentile' },
];

/** Nom de la feuille de distribution anonymisée (≤ 31 caractères, contrainte XLSX). */
export const DISTRIBUTION_SHEET_NAME = 'Distribution anonymisée';
/** Nom de la feuille de ranking nominatif. */
export const RANKING_SHEET_NAME = 'Ranking nominatif';

/**
 * Clés journalisées d'un export (une entrée de journal par FICHIER, DBU §2.6 v2.8). Les clés sont
 * PRÉFIXÉES par le tableau d'origine : un classeur à deux feuilles journalise les deux préfixes dans la
 * MÊME entrée (parité du patron `prixCibleMedianes.*` déjà en place).
 */
export const DISTRIBUTION_JOURNAL_KEYS: readonly string[] =
  DISTRIBUTION_EXPORT_COLUMNS.map((c) => `distribution.${c.key}`);
export const RANKING_JOURNAL_KEYS: readonly string[] = RANKING_EXPORT_COLUMNS.map(
  (c) => `ranking.${c.key}`,
);

/** Valeur BRUTE d'une cellule de distribution : string pour les codes, number|'' pour les bornes. */
function distributionCell(
  key: string,
  row: AnalysisPercentileRowDto,
): string | number {
  // Ligne supprimée par la K-anonymity : toutes les bornes sont VIDES (jamais 0).
  const bound = (value: number | null): string | number =>
    row.suppressed || value === null ? '' : value;
  switch (key) {
    case 'profileCode':
      return row.profileCode;
    case 'seniorityLevel':
      return seniorityLabel(row.seniorityLevel);
    case 'minEur':
      return bound(row.minEur);
    case 'p10Eur':
      return bound(row.p10Eur);
    case 'p25Eur':
      return bound(row.p25Eur);
    case 'p50Eur':
      return bound(row.p50Eur);
    case 'p75Eur':
      return bound(row.p75Eur);
    case 'p90Eur':
      return bound(row.p90Eur);
    case 'maxEur':
      return bound(row.maxEur);
    default:
      return '';
  }
}

/** Valeur BRUTE d'une cellule de ranking. Métrique absente OU masquée ⇒ cellule VIDE, JAMAIS 0. */
function rankingCell(key: string, view: RankingViewRow): string | number {
  const row = view.row;
  const num = (value: number | null | undefined): string | number =>
    value === null || value === undefined ? '' : value;
  switch (key) {
    case 'sousFamille':
      // Sous-famille inconnue ⇒ cellule VIDE (parité écran, où la colonne affiche « — »).
      return view.sousFamille ?? '';
    case 'profileCode':
      return row.profileCode;
    case 'seniorityLevel':
      return seniorityLabel(row.seniorityLevel);
    case 'responseC1Eur':
      return num(row.responseC1Eur);
    case 'responseC2Eur':
      return num(row.responseC2Eur);
    case 'ecartP50Pct':
      // Masqué K-anon ⇒ vide (parité écran : un tiret n'est pas une valeur).
      return view.ecartMedianeKind === 'value' ? num(row.ecartP50Pct) : '';
    case 'ecartTargetPricePct':
      return num(row.ecartTargetPricePct);
    case 'raisonSociale':
      return row.raisonSociale;
    case 'siret':
      return row.supplierSiret;
    case 'rank':
      // Libellé DOUBLÉ (jamais le code seul) via le mapping existant rank-labels.
      return RANK_PRESENTATION[row.rank].label;
    case 'percentile':
      return view.percentileKind === 'value' ? num(row.percentile) : '';
    default:
      return '';
  }
}

/** Matrice (en-tête + lignes) d'un tableau — base commune CSV/XLSX (une seule source d'ordre). */
function toMatrix<T>(
  columns: readonly BenchmarkExportColumn[],
  rows: readonly T[],
  cell: (key: string, row: T) => string | number,
): (string | number)[][] {
  return [
    columns.map((col) => col.label),
    ...rows.map((row) => columns.map((col) => cell(col.key, row))),
  ];
}

/** Sérialise une matrice en CSV (RFC 4180, lignes CRLF, anti-injection-formule OBS-1). */
function matrixToCsv(matrix: readonly (string | number)[][]): string {
  return matrix
    .map((line) => line.map((cell) => csvEscapeCell(String(cell))).join(','))
    .join('\r\n');
}

/** CSV de la feuille « Distribution anonymisée » (fichier DÉDIÉ — décision 12, CSV = un fichier par case). */
export function buildDistributionCsv(
  rows: readonly AnalysisPercentileRowDto[],
): string {
  return matrixToCsv(
    toMatrix(DISTRIBUTION_EXPORT_COLUMNS, rows, distributionCell),
  );
}

/** CSV de la feuille « Ranking nominatif » (fichier DÉDIÉ). */
export function buildRankingCsv(rows: readonly RankingViewRow[]): string {
  return matrixToCsv(toMatrix(RANKING_EXPORT_COLUMNS, rows, rankingCell));
}

/** Tableaux à écrire dans le classeur : une feuille par case cochée (au moins une). */
export interface AnalysisXlsxInput {
  readonly distribution?: readonly AnalysisPercentileRowDto[] | undefined;
  readonly ranking?: readonly RankingViewRow[] | undefined;
}

/**
 * Écrit les octets .xlsx (SheetJS, patron worker `deliverable-xlsx.writer.ts` : `aoa_to_sheet` + `write`).
 * `type: 'array'` ⇒ ArrayBuffer (navigateur-safe, BlobPart direct). UN classeur, UNE feuille par case
 * cochée, dans l'ordre « Distribution anonymisée » puis « Ranking nominatif ».
 */
export function buildAnalysisXlsx(input: AnalysisXlsxInput): ArrayBuffer {
  const workbook = XLSX.utils.book_new();
  if (input.distribution !== undefined) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(
        toMatrix(DISTRIBUTION_EXPORT_COLUMNS, input.distribution, distributionCell),
      ),
      DISTRIBUTION_SHEET_NAME,
    );
  }
  if (input.ranking !== undefined) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(
        toMatrix(RANKING_EXPORT_COLUMNS, input.ranking, rankingCell),
      ),
      RANKING_SHEET_NAME,
    );
  }
  return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

/** Tableau exporté (identifie le fichier CSV produit ; `classeur` = XLSX à une ou deux feuilles). */
export type AnalysisExportTable = 'distribution' | 'ranking' | 'classeur';

/** Nom de fichier explicite et horodaté (audit externe). Un fichier par tableau en CSV. */
export function analysisExportFileName(
  table: AnalysisExportTable,
  format: 'csv' | 'xlsx',
  now: Date = new Date(),
): string {
  const iso = now.toISOString().slice(0, 10);
  const suffix =
    table === 'distribution'
      ? 'distribution-anonymisee'
      : table === 'ranking'
        ? 'ranking-nominatif'
        : 'analyse';
  return `benchmark-${suffix}-${iso}.${format}`;
}
